import React, { useState, useEffect, useMemo, useRef } from "react";
import "./css/MultiSelectFilter.css";

const MultiSelectFilter = ({
    label,
    options = [],
    selected = [],
    setSelected,
    placeholder = "Select...",
    pinSelectedToTop = false
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchText, setSearchText] = useState("");
    const dropdownRef = useRef(null);

    const selectedSet = useMemo(
        () => new Set(selected.map((value) => String(value))),
        [selected]
    );

    // Filter options based on search text and optionally pin selected values.
    const filteredOptions = useMemo(() => {
        const normalizedSearch = searchText.trim().toLowerCase();
        const selectedOptions = [];
        const regularOptions = [];

        options.forEach((option) => {
            const optionValue = String(option?.value ?? "");
            const optionLabel = String(option?.label ?? "");
            const matchesSearch = !normalizedSearch || optionLabel.toLowerCase().includes(normalizedSearch);
            const isSelected = selectedSet.has(optionValue);

            if (pinSelectedToTop && isSelected) {
                selectedOptions.push(option);
                return;
            }

            if (matchesSearch) {
                regularOptions.push(option);
            }
        });

        if (pinSelectedToTop) {
            return [...selectedOptions, ...regularOptions];
        }

        return regularOptions;
    }, [options, pinSelectedToTop, searchText, selectedSet]);

    const selectionLabel = useMemo(() => {
        if (selected.length === 0) return placeholder;

        const selectedLabels = selected
            .map((value) => options.find((option) => String(option.value) === String(value))?.label)
            .filter(Boolean);

        if (selectedLabels.length > 0 && selectedLabels.length <= 2) {
            return selectedLabels.join(", ");
        }

        return `${selected.length} selected`;
    }, [options, placeholder, selected]);

    // Toggle selection properly
    const handleOptionToggle = (optionValue) => {
        setSelected(prevSelected => {
            if (prevSelected.includes(optionValue)) {
                return prevSelected.filter(item => item !== optionValue); // Remove if already selected
            } else {
                return [...prevSelected, optionValue]; // Add if not selected
            }
        });
    };

    // Handle outside click to close dropdown
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    return (
        <div className="filter-group" ref={dropdownRef}>
            <label>{label}</label>
            <div className="dropdown">
                <button type="button" onClick={() => setIsOpen(!isOpen)}>
                    {selectionLabel}
                </button>
                {isOpen && (
                    <div className="dropdown-content">
                        <input
                            type="text"
                            className="search-box"
                            placeholder="Search..."
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                        />
                        <ul className="filter-options">
                            {filteredOptions.map(option => (
                                <li key={option.value}>
                                    <label>
                                        <input
                                            type="checkbox"
                                            checked={selected.includes(option.value)} // ✅ Always reflects correct state
                                            onChange={() => handleOptionToggle(option.value)} // ✅ Fixes checkbox toggling issue
                                        />
                                        {option.label}
                                    </label>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
            </div>
        </div>
    );
};

export default MultiSelectFilter;
